import React from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";

import CrmShell from "../components/CrmShell";
import {
  CrmPageHeader,
  CrmPanel,
  MetricCard,
  ResponsiveGrid,
  crmUi,
} from "../components/CrmUi";
import MiniLineChart from "../components/MiniLineChart";
import DealPipeline from "../components/DealPipeline";
import { activities, organizations } from "../demoData";

const CrmOverviewPage = () => {
  const staleOrganizations = organizations.filter(x => x.status === "Behöver uppföljning");

  return (
    <CrmShell>
      <CrmPageHeader
        title="CRM – Översikt"
        subtitle="Relationer, affärer och aktiviteter i organisationens CRM."
        actions={[
          <Button key="period" size="small" variant="outlined" sx={{ textTransform: "none" }}>
            Denna månad
          </Button>,
        ]}
      />

      <ResponsiveGrid>
        {[
          ["Organisationer", "248", "", <ApartmentOutlinedIcon />, "primary"],
          ["Direkta kunder", "96", "", <PeopleAltOutlinedIcon />, "success"],
          ["Affärsnätverk", "18", "", <AccountTreeOutlinedIcon />, "primary"],
          ["Öppna affärer", "14", "", <HandshakeOutlinedIcon />, "warning"],
          ["Aktiviteter", "23", "", <ChecklistRoundedIcon />, "primary"],
          ["Avtal förfaller snart", "6", "", <EventBusyOutlinedIcon />, "danger"],
        ].map(([label, value, change, icon, tone]) => (
          <Grid item xs={12} sm={6} lg={4} xl={2} key={label}>
            <MetricCard label={label} value={value} change={change} icon={icon} tone={tone} />
          </Grid>
        ))}
      </ResponsiveGrid>

      <Grid container spacing={1.15} sx={{ mt: 0.05 }}>
        <Grid item xs={12} xl={6}>
          <CrmPanel sx={{ p: 1.5, height: "100%" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography sx={{ color: crmUi.text, fontSize: 13.4, fontWeight: 550 }}>
                  Aktivitet senaste 30 dagarna
                </Typography>
                <Typography sx={{ mt: 0.15, color: crmUi.muted, fontSize: 10.4 }}>
                  Aktiviteter och skapade affärer
                </Typography>
              </Box>
              <Typography sx={{ color: crmUi.subtle, fontSize: 9.8 }}>Senaste 6 veckorna</Typography>
            </Stack>
            <MiniLineChart />
          </CrmPanel>
        </Grid>

        <Grid item xs={12} md={7} xl={3}>
          <CrmPanel sx={{ p: 1.5, height: "100%" }}>
            <Typography sx={{ color: crmUi.text, fontSize: 13.4, fontWeight: 550 }}>
              Organisationer utan aktivitet
            </Typography>
            <Typography sx={{ mt: 0.15, color: crmUi.muted, fontSize: 10.4 }}>
              Ingen aktivitet senaste 60 dagarna
            </Typography>
            <List dense disablePadding sx={{ mt: 0.8 }}>
              {staleOrganizations.concat([
                { name: "Efterbehandlare Delta", lastActivity: "68 dagar" },
                { name: "IDP-Test", lastActivity: "64 dagar" },
              ]).slice(0, 5).map(item => (
                <ListItem key={item.name} disableGutters sx={{ py: 0.55 }}>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{ fontSize: 10.8, color: crmUi.text }}
                  />
                  <Typography sx={{ color: crmUi.muted, fontSize: 9.8 }}>{item.lastActivity}</Typography>
                </ListItem>
              ))}
            </List>
            <Button size="small" sx={{ px: 0, textTransform: "none", fontSize: 10.4 }}>
              Visa alla organisationer
            </Button>
          </CrmPanel>
        </Grid>

        <Grid item xs={12} md={5} xl={3}>
          <CrmPanel sx={{ p: 1.5, height: "100%" }}>
            <Typography sx={{ color: crmUi.text, fontSize: 13.4, fontWeight: 550 }}>
              Insikt
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ color: "primary.main", fontSize: 34, fontWeight: 560, lineHeight: 1 }}>
                12
              </Typography>
              <Typography sx={{ mt: 0.6, color: crmUi.text, fontSize: 13, fontWeight: 520, maxWidth: 180 }}>
                organisationer debiteras via överordnad organisation
              </Typography>
            </Box>
          </CrmPanel>
        </Grid>

        <Grid item xs={12} lg={4}>
          <CrmPanel sx={{ p: 1.5, height: "100%" }}>
            <Typography sx={{ color: crmUi.text, fontSize: 13.4, fontWeight: 550 }}>
              Affärspipeline
            </Typography>
            <Box sx={{ mt: 1.1 }}><DealPipeline compact /></Box>
          </CrmPanel>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <CrmPanel sx={{ p: 1.5, height: "100%" }}>
            <Typography sx={{ color: crmUi.text, fontSize: 13.4, fontWeight: 550 }}>
              Kommande uppgifter
            </Typography>
            <List dense disablePadding sx={{ mt: 0.55 }}>
              {activities.filter(x => x.status !== "Klar").slice(0, 5).map(item => (
                <React.Fragment key={item.id}>
                  <ListItem disableGutters sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={item.title}
                      secondary={item.organization}
                      primaryTypographyProps={{ fontSize: 10.6, color: crmUi.text, noWrap: true }}
                      secondaryTypographyProps={{ fontSize: 9.4, color: crmUi.subtle }}
                    />
                    <Typography sx={{ ml: 1, color: crmUi.muted, fontSize: 9.5, flexShrink: 0 }}>
                      {item.when}
                    </Typography>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </CrmPanel>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <CrmPanel sx={{ p: 1.5, height: "100%" }}>
            <Typography sx={{ color: crmUi.text, fontSize: 13.4, fontWeight: 550 }}>
              Senaste aktivitet
            </Typography>
            <List dense disablePadding sx={{ mt: 0.55 }}>
              {activities.filter(x => x.status === "Klar").slice(0, 5).map(item => (
                <ListItem key={item.id} disableGutters sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={item.title}
                    secondary={item.organization}
                    primaryTypographyProps={{ fontSize: 10.6, color: crmUi.text, noWrap: true }}
                    secondaryTypographyProps={{ fontSize: 9.4, color: crmUi.subtle }}
                  />
                  <Typography sx={{ ml: 1, color: crmUi.muted, fontSize: 9.5, flexShrink: 0 }}>
                    {item.when}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </CrmPanel>
        </Grid>
      </Grid>
    </CrmShell>
  );
};

export default CrmOverviewPage;
